import { i18n } from "@lingui/core";
import { t, Trans } from "@lingui/macro";
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Typography } from "@mui/material";
import {
    MatesMultiSelect,
    MatesSelect,
    MatesTextField,
    withUseFormHook
} from "@sinossi/mates-react-library";
import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { AssetType, Brand, BrandPriority, ContractType, PositionType, SwitchEnum } from "../../../../models";
import EditResearchValidation, { UpdateResearchForm } from './form';

interface EditResearchProps extends UseFormReturn {
    open: boolean
    onClose: () => void
    data: BrandPriority | null
    type: string
    duplicate?: boolean
    submit: (request: any) => void
    brands: Brand[]
}

interface EditResearchState {
    isFormLoaded: boolean
}

class EditResearch extends React.Component<EditResearchProps, EditResearchState> {

    private priority: { id: string, priority: string }[] = [
        { id: "URGENT", priority: "URGENTE" },
        { id: "HIGH", priority: "ALTA" },
        { id: "NORMAL", priority: "NORMALE" },
        { id: "LOW", priority: "BASSA" },
    ]


    constructor(props: EditResearchProps) {
        super(props)

        this.state = {
            isFormLoaded: false,
        }
    }

    componentDidMount() {
        this.props.reset(new UpdateResearchForm())
        this.setFormValues()
    }

    private setFormValues = (): void => {

        if (this.props.type === "creation") {
            return
        }

        const formData = UpdateResearchForm.fromServerResponse(this.props.data)

        this.props.reset(formData)

        this.setState(() => ({ isFormLoaded: true }))
    }

    private contractTypeEntries = () => {
        const typeEntries: Record<string, string> = {}

        for (const typeKey in ContractType) {
            typeEntries[typeKey] = /*i18n*/ i18n._("enums.contract-type." + typeKey)
        }
        return typeEntries
    }

    render() {
        const assetTypeOptions = Object.values(AssetType).map((type: any) => (
            <option key={type} value={type}>
                <Trans id={`enums.asset-types.${type}`}>
                </Trans>
            </option>
        ))

        return (
            <Dialog open={this.props.open} onClose={this.props.onClose} maxWidth={"lg"}>
                <Box sx={{ m: 2 }}>
                    {
                        this.props.type === "edit" && !this.props.duplicate &&
                        <Typography variant="h5">
                            <Trans id={'opening.edit-research.title'}>
                                Modifica Apertura
                            </Trans>
                        </Typography>
                    }
                    {
                        this.props.type === "creation" &&
                        <Typography variant="h5">
                            <Trans id={'opening.create-research.title'}>
                                Crea Apertura
                            </Trans>
                        </Typography>
                    }
                    {
                        this.props.duplicate &&
                        <Typography variant="h5">
                            <Trans id={'opening.duplicate-research.title'}>
                                Copia Apertura
                            </Trans>
                        </Typography>
                    }
                    <form onSubmit={this.props.handleSubmit(this.props.submit)}>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item xs={6}>
                                    <MatesTextField variant={"outlined"}
                                        label={t({
                                            id: "opening.edit-research.name",
                                            message: "Nome apertura",
                                        }
                                        )}
                                        name="name"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MatesSelect variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.brandId",
                                            message: "Brand",
                                        }
                                        )}
                                        name="brandId"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        disabled={this.props.type === "edit" && !this.props.duplicate}
                                        required
                                    >
                                        {this.props.brands.map((brand) => (
                                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                                        ))}
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={6}>
                                    <MatesTextField variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.region",
                                            message: "Regione",
                                        }
                                        )}
                                        name="region"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MatesTextField variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.city",
                                            message: "Comune",
                                        }
                                        )}
                                        name="city"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        disabled={this.props.getValues("region") === undefined || this.props.getValues("region") === ""}
                                    />
                                </Grid>
                                <Grid item xs={8}>
                                    <MatesTextField variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.zone",
                                            message: "Via/Zona",
                                        }
                                        )}
                                        name="street"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                        disabled={this.props.getValues("city") === undefined || this.props.getValues("city") === ""}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <MatesSelect variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.priority",
                                            message: "Livello priorità",
                                        }
                                        )}
                                        name="priorityLevel"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    >
                                        {this.priority.map((it) => (
                                            <option key={it.id} value={it.priority}>{it.priority}</option>
                                        ))}
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={4}>
                                    <MatesMultiSelect
                                        name="preferredContractTypes"
                                        label={t({
                                            id: "research.edit-research.contracts",
                                            message: "Tipologia contratto",
                                        })}
                                        variant="outlined"
                                        control={this.props.control}
                                        formState={this.props.formState}
                                        trigger={this.props.trigger}
                                        entries={this.contractTypeEntries()}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <MatesSelect
                                        name="positionType"
                                        label={t({
                                            id: "research.edit-research.position",
                                            message: "Posizione",
                                        })}
                                        variant="outlined"
                                        control={this.props.control}
                                        formState={this.props.formState}
                                        trigger={this.props.trigger}
                                    >
                                        {
                                            Object.keys(PositionType).map((typeKey, index) =>
                                                <option value={typeKey} key={index}>
                                                    <Trans id={`enums.asset-position.${typeKey}`}>
                                                    </Trans>
                                                </option>
                                            )
                                        }
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={4}>
                                    <MatesTextField variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.max-price",
                                            message: `Prezzo massimo (€)`,
                                        }
                                        )}
                                        name="maxPrice"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <MatesTextField
                                        name="minDim"
                                        label="Dimensione minima (mq)"
                                        variant="outlined"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    />
                                </Grid>
                                <Grid item xs={6} md={4}>
                                    <MatesTextField
                                        name="maxDim"
                                        label="Dimensione massima (mq)"
                                        variant="outlined"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <MatesSelect
                                        variant={"outlined"}
                                        label={t({
                                            id: "research.edit-research.favourite-property-type",
                                            message: "Tipo di proprietà",
                                        })}
                                        name="propertyType"
                                        control={this.props.control}
                                        trigger={this.props.trigger}
                                        formState={this.props.formState}
                                    >
                                        {assetTypeOptions}
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={3}>
                                    <MatesSelect
                                        name="airConditioning"
                                        label={t({ id: "research.edit-research.air-conditioning", message: "Aria condizionata" })}
                                        variant="outlined"
                                        formState={this.props.formState}
                                        control={this.props.control}
                                        trigger={this.props.trigger}>
                                        {
                                            Object.keys(SwitchEnum).map((typeKey, index) =>
                                                <option value={typeKey} key={index}>
                                                    <Trans id={`enums.switch-enum.${typeKey}`}>
                                                    </Trans>
                                                </option>
                                            )
                                        }
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={3}>
                                    <MatesSelect
                                        name="shopWindows"
                                        label={t({ id: "research.edit-research.windows", message: "Vetrine" })}
                                        variant="outlined"
                                        formState={this.props.formState}
                                        control={this.props.control}
                                        trigger={this.props.trigger}>
                                        {
                                            Object.keys(SwitchEnum).map((typeKey, index) =>
                                                <option value={typeKey} key={index}>
                                                    <Trans id={`enums.switch-enum.${typeKey}`}>
                                                    </Trans>
                                                </option>
                                            )
                                        }
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={3}>
                                    <MatesSelect
                                        name="parkingSlots"
                                        label={t({ id: "research.edit-research.parking", message: "Parcheggi" })}
                                        variant="outlined"
                                        formState={this.props.formState}
                                        control={this.props.control}
                                        trigger={this.props.trigger}>
                                        {
                                            Object.keys(SwitchEnum).map((typeKey, index) =>
                                                <option value={typeKey} key={index}>
                                                    <Trans id={`enums.switch-enum.${typeKey}`}>
                                                    </Trans>
                                                </option>
                                            )
                                        }
                                    </MatesSelect>
                                </Grid>
                                <Grid item xs={3}>
                                    <MatesSelect
                                        name="singleLevel"
                                        label={t({ id: "research.edit-research.single-floor", message: "Singolo piano" })}
                                        variant="outlined"
                                        formState={this.props.formState}
                                        control={this.props.control}
                                        trigger={this.props.trigger}>
                                        {
                                            Object.keys(SwitchEnum).map((typeKey, index) =>
                                                <option value={typeKey} key={index}>
                                                    <Trans id={`enums.switch-enum.${typeKey}`}>
                                                    </Trans>
                                                </option>
                                            )
                                        }
                                    </MatesSelect>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={this.props.onClose}
                                variant={"contained"}
                                color="neutral"
                                sx={{
                                    textTransform: "none",
                                    mr: 1,
                                    width: 100
                                }}
                                disableElevation>
                                <Trans id={"workflow.template.new-template.abort"}>
                                    Annulla
                                </Trans>
                            </Button>
                            <Button type="submit"
                                variant="contained"
                                sx={{
                                    backgroundColor: 'secondary.main',
                                    color: 'white',
                                    mr: 1,
                                    textTransform: "none",
                                    width: 100
                                }}
                            >
                                {
                                    (this.props.duplicate || this.props.type === "creation") &&
                                    <Trans id={"edit-research.create.save"}>Crea</Trans>
                                }
                                {
                                    this.props.type === "edit" && !this.props.duplicate &&
                                    <Trans id={"edit-research.update.save"}>Aggiorna</Trans>
                                }
                            </Button>
                        </DialogActions>
                    </form>
                </Box>
            </Dialog>
        )
    }
}

export default withUseFormHook<UpdateResearchForm>(
    EditResearch,
    EditResearchValidation,
    new UpdateResearchForm()
)
